import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import apiClient from '../services/apiClient';

const PERMISSIONS_STORAGE_KEY = 'taminzo_permissions';

interface PermissionsContextType {
  permissions: string[];
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  refetchPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const [permissions, setPermissions] = useState<string[]>(() => {
    // Load from localStorage on init for instant availability on page reload
    try {
      const stored = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [loading, setLoading] = useState(() => {
    // Only show loading if there's nothing cached
    return !localStorage.getItem(PERMISSIONS_STORAGE_KEY);
  });

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await apiClient.getAuthClient().get('/auth/my-permissions');
      const perms: string[] = response.data.permissions || [];
      setPermissions(perms);
      localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(perms));
    } catch (error) {
      console.error('[PermissionsContext] Failed to fetch permissions:', error);
      // On error, clear stale permissions so we don't show incorrect access
      setPermissions([]);
      localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      // Clear stale cached permissions before fetching fresh ones
      setPermissions([]);
      setLoading(true);
      fetchPermissions();
    } else {
      // User logged out — clear permissions
      setPermissions([]);
      localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
      setLoading(false);
    }
  }, [isAuthenticated, fetchPermissions]);

  const hasPermission = useCallback(
    (permission: string) => permissions.includes(permission),
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (perms: string[]) => perms.some((p) => permissions.includes(p)),
    [permissions]
  );

  const hasAllPermissions = useCallback(
    (perms: string[]) => perms.every((p) => permissions.includes(p)),
    [permissions]
  );

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        loading,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        refetchPermissions: fetchPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};
